/**
 * The canonical origin, scheme included, normalised, with a fallback.
 *
 * `CANONICAL_DOMAIN` (host only, no scheme) is what most consumers have always
 * used, and it is `undefined` whenever `NEXT_PUBLIC_CANONICAL_DOMAIN` is unset.
 * Call sites that interpolated it straight into a template produced
 * `https://undefined/nl/blog/my-post` in that case, which is why hardcoding the
 * host was the safer option for anyone writing a canonical tag. The fallback
 * here removes that trade-off: `CANONICAL_HOST` is always a usable origin.
 *
 * The value is also normalised, which closes the last failure mode WHO-5133
 * listed. The env var wants a bare host, but the natural thing to paste is a
 * full URL, and `https://${"https://www.whoppah.com"}` parses to the host
 * `https` and emits `https://https/furniture/chairs` on every page with nothing
 * raised anywhere. A supplied scheme is therefore stripped, as is a trailing
 * slash, and a value that is blank or only whitespace falls back rather than
 * producing an empty host that makes `new URL` throw.
 *
 * CJS and dependency-free so `config/redirects.js` can require it at Node level;
 * `src/config.js` re-exports it for bundled code.
 */
const FALLBACK_HOST = "www.whoppah.com";

const normaliseHost = value => {
  if (typeof value !== "string") {
    return FALLBACK_HOST;
  }

  const host = value
    .trim()
    // A supplied scheme, or a protocol-relative `//host`, or both.
    .replace(/^[a-z][a-z0-9+.-]*:/i, "")
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  return host === "" ? FALLBACK_HOST : host;
};

const CANONICAL_HOST = `https://${normaliseHost(
  process.env.NEXT_PUBLIC_CANONICAL_DOMAIN
)}`;

module.exports = { CANONICAL_HOST, normaliseHost };
